/**
 * Helpers for the single client error-reporting site (`<RouterProvider onError>`
 * in `main.tsx`).
 *
 * ## Why stale-chunk errors are normalized
 *
 * When a tab runs a pre-deploy `index.html`, the hashed route chunk it asks for
 * is gone. Every browser words that failure differently, and — critically —
 * most wordings EMBED THE CHUNK URL:
 *
 *     Failed to fetch dynamically imported module: https://app.ordo.com/assets/family-checkout-6WItOIvN.js
 *
 * The Loki alert rule groups by the raw message, so each chunk hash mints its
 * own Slack alert, and every deploy mints fresh hashes. Measured: 227 distinct
 * chunk URLs over 30 days, 699 of the app's 752 distinct exception values —
 * an unbounded, self-renewing alert stream for what is ONE condition.
 *
 * So we report a fixed string and move the varying URL into `context`, which is
 * not part of the alert's grouping key. One condition, one alert.
 *
 * This became load-bearing the moment the server started returning a real 404
 * for missing `/assets/*` (see `server/src/app.ts`). Before that, the dominant
 * wording was the hash-free `'text/html' is not a valid JavaScript MIME type`;
 * after it, the browser reports the URL-bearing wording instead. Without this
 * normalization the 404 fix REDUCES events while INCREASING alert count.
 */

/**
 * Every browser wording for "a lazy chunk failed to load", including the
 * pre-404 MIME variants that a client on an old build can still hit while a
 * rollout is in flight.
 *
 * Chrome/Edge: "Failed to fetch dynamically imported module: <url>"
 * Firefox:     "error loading dynamically imported module: <url>"
 * Safari:      "Importing a module script failed." (no URL)
 * Pre-404:     "'text/html' is not a valid JavaScript MIME type[ for module script '<url>']."
 * Vite CSS:    "Unable to preload CSS for <url>"
 */
const STALE_CHUNK_PATTERN =
  /dynamically imported module|Importing a module script failed|module script failed|is not a valid JavaScript MIME type|Unable to preload CSS/i

/** The one message every stale-chunk failure reports as. Keep it stable. */
export const STALE_CHUNK_MESSAGE = 'stale route chunk failed to load'

/** Whether a message is any browser's phrasing of a failed lazy-chunk load. */
export function isStaleChunkMessage(message: string): boolean {
  return STALE_CHUNK_PATTERN.test(message)
}

/**
 * Best-effort message for a thrown value.
 *
 * `pushError` coerces non-Errors with `String(error)`, which renders a
 * react-router `ErrorResponse` as the literal `[object Object]` — a useless
 * alert. Not reachable today (no route throws a Response), so this is cheap
 * insurance against the first one that does.
 */
export function describeThrown(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (error !== null && typeof error === 'object' && 'status' in error) {
    const response = error as { status?: unknown; statusText?: unknown }
    return `Route error ${String(response.status)} ${String(response.statusText ?? '')}`.trim()
  }
  return String(error)
}
